import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";

export interface PasswordData {
  password: string;
  confirmPassword: string;
}

export interface PasswordValidations {
  noSpecialChars: boolean;
  minLength: boolean;
  hasLettersAndTwoNumbers: boolean;
  passwordsMatch: boolean;
}

export function validatePassword(data: PasswordData): PasswordValidations {
  return {
    noSpecialChars: /^[a-zA-Z0-9]*$/.test(data.password),
    minLength: data.password.length >= 6,
    hasLettersAndTwoNumbers:
      /[a-zA-Z]/.test(data.password) &&
      (data.password.match(/[0-9]/g) ?? []).length >= 2,
    passwordsMatch:
      data.password !== "" && data.password === data.confirmPassword,
  };
}

export function isPasswordValid(data: PasswordData): boolean {
  return Object.values(validatePassword(data)).every(Boolean);
}

interface PasswordFieldsProps {
  value: PasswordData;
  onChange: (value: PasswordData) => void;
  disabled?: boolean;
}

const PasswordFields = ({ value, onChange, disabled = false }: PasswordFieldsProps) => {
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validations = validatePassword(value);

  const rules: { key: keyof PasswordValidations; label: string }[] = [
    { key: "noSpecialChars", label: t("setPassword.ruleNoSpecialChars") },
    { key: "minLength", label: t("setPassword.ruleMinLength") },
    { key: "hasLettersAndTwoNumbers", label: t("setPassword.ruleLettersAndNumbers") },
    { key: "passwordsMatch", label: t("setPassword.rulePasswordsMatch") },
  ];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="password" className="font-medium text-gray-700">
          {t("setPassword.passwordLabel")}
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={value.password}
            onChange={(e) => onChange({ ...value, password: e.target.value })}
            className="h-9 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14] pr-10"
            placeholder="••••••••"
            required
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            disabled={disabled}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className="font-medium text-gray-700">
          {t("setPassword.confirmPasswordLabel")}
        </Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={value.confirmPassword}
            onChange={(e) => onChange({ ...value, confirmPassword: e.target.value })}
            className="h-9 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14] pr-10"
            placeholder="••••••••"
            required
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            disabled={disabled}
          >
            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-1.5 py-1">
        {rules.map(({ key, label }) => {
          const ok = validations[key];
          return (
            <div key={key} className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full shrink-0 ${ok ? "bg-green-500" : "bg-gray-300"}`} />
              <span className={ok ? "text-green-600" : "text-gray-400"}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PasswordFields;
